import React, { useState, useEffect, useRef } from "react";
import "../../styles/accessibility.css";

const AccessibilityPanel = () => {
  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [fontSize, setFontSize] = useState(1);
  
  // Set highContrast to false by default - this is the key change
  const [highContrast, setHighContrast] = useState(false);
  
  const [dyslexiaFriendly, setDyslexiaFriendly] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [focusVisible, setFocusVisible] = useState(true);
  const [voiceNavigation, setVoiceNavigation] = useState(false);
  const panelRef = useRef(null);

  // Apply accessibility settings when they change
  useEffect(() => {
    const htmlElement = document.documentElement;
    
    // Font size
    htmlElement.classList.remove('access-font-size-1', 'access-font-size-2', 'access-font-size-3', 'access-font-size-4', 'access-font-size-5');
    htmlElement.classList.add(`access-font-size-${fontSize}`);
    
    // High contrast - only apply when explicitly enabled
    if (highContrast) {
      htmlElement.classList.add('access-high-contrast');
    } else {
      htmlElement.classList.remove('access-high-contrast');
    }
    
    // Dyslexia friendly
    if (dyslexiaFriendly) {
      htmlElement.classList.add('access-dyslexia-friendly');
    } else {
      htmlElement.classList.remove('access-dyslexia-friendly');
    }
    
    // Reduced motion
    if (reducedMotion) {
      htmlElement.classList.add('access-reduced-motion');
    } else {
      htmlElement.classList.remove('access-reduced-motion');
    }
    
    // Focus visible
    if (focusVisible) {
      htmlElement.classList.add('access-focus-visible');
    } else {
      htmlElement.classList.remove('access-focus-visible');
    }
  }, [fontSize, highContrast, dyslexiaFriendly, reducedMotion, focusVisible]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target) && !event.target.classList.contains('access-toggle-button')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Modified to NOT automatically set high contrast based on user preferences
  useEffect(() => {
    // Check for prefers-reduced-motion only
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setReducedMotion(true);
    }
    
    // Removed the dark mode preference check that was setting highContrast
  }, []);

  // Reset all settings
  const handleReset = () => {
    setFontSize(1);
    setHighContrast(false);
    setDyslexiaFriendly(false);
    setReducedMotion(false);
    setFocusVisible(true);
    setVoiceNavigation(false);
  };

  // Font size controls
  const increaseFontSize = () => {
    if (fontSize < 5) {
      setFontSize(fontSize + 1);
    }
  };

  const decreaseFontSize = () => {
    if (fontSize > 1) {
      setFontSize(fontSize - 1);
    }
  };

  // Toggle voice navigation (simulated)
  const toggleVoiceNavigation = () => {
    setVoiceNavigation(!voiceNavigation);
    if (!voiceNavigation) {
      // Show toast notification
      const toast = document.createElement('div');
      toast.className = 'access-toast';
      toast.textContent = 'Voice navigation activated. Say "help" for commands.';
      document.body.appendChild(toast);
      
      setTimeout(() => {
        toast.classList.add('access-toast-visible');
      }, 100);
      
      setTimeout(() => {
        toast.classList.remove('access-toast-visible');
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 500);
      }, 5000);
    }
  };

  return (
    <>
      {/* Skip to content link */}
      <a href="#main-content" className="access-skip-link">
        Skip to main content
      </a>
      
      {/* Accessibility controls container */}
      <div className="access-controls-container">
        <button 
          className="access-toggle-button"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-label="Accessibility options"
        >
          <i className="bi bi-universal-access access-icon"></i>
          <span className="access-sr-only">Accessibility Options</span>
        </button>
        
        {isOpen && (
          <div className="access-panel" ref={panelRef}>
            <div className="access-panel-header">
              <h2 className="access-panel-heading">Accessibility Options</h2>
              <button 
                className="access-close-btn"
                onClick={() => setIsOpen(false)}
                aria-label="Close accessibility panel"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            
            {/* Font size controls */}
            <div className="access-control-group">
              <label className="access-control-label">Text Size</label>
              <div className="access-font-controls">
                <button 
                  className="access-font-btn" 
                  onClick={decreaseFontSize}
                  disabled={fontSize <= 1}
                  aria-label="Decrease font size"
                >
                  <i className="bi bi-dash-lg"></i>
                </button>
                <span className="access-font-size-display">{fontSize * 100}%</span>
                <button 
                  className="access-font-btn" 
                  onClick={increaseFontSize}
                  disabled={fontSize >= 5}
                  aria-label="Increase font size"
                >
                  <i className="bi bi-plus-lg"></i>
                </button>
              </div>
            </div>
            
            {/* Toggle switches */}
            <div className="access-control-group">
              <label className="access-control-label">Display Options</label>
              
              <div className="access-toggle-switch-wrapper">
                <span className="access-toggle-label">
                  <i className="bi bi-circle-half access-toggle-icon"></i>
                  High Contrast
                </span>
                <label className="access-toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={highContrast}
                    onChange={() => setHighContrast(!highContrast)}
                    aria-label="Toggle high contrast mode"
                  />
                  <span className="access-toggle-slider"></span>
                </label>
              </div>
              
              <div className="access-toggle-switch-wrapper">
                <span className="access-toggle-label">
                  <i className="bi bi-type access-toggle-icon"></i>
                  Dyslexia Friendly
                </span>
                <label className="access-toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={dyslexiaFriendly}
                    onChange={() => setDyslexiaFriendly(!dyslexiaFriendly)}
                    aria-label="Toggle dyslexia friendly text"
                  />
                  <span className="access-toggle-slider"></span>
                </label>
              </div>
              
              <div className="access-toggle-switch-wrapper">
                <span className="access-toggle-label">
                  <i className="bi bi-pause-circle access-toggle-icon"></i>
                  Reduce Motion
                </span>
                <label className="access-toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={reducedMotion}
                    onChange={() => setReducedMotion(!reducedMotion)}
                    aria-label="Toggle reduced motion"
                  />
                  <span className="access-toggle-slider"></span>
                </label>
              </div>
              
              <div className="access-toggle-switch-wrapper">
                <span className="access-toggle-label">
                  <i className="bi bi-border-style access-toggle-icon"></i>
                  Focus Indicators
                </span>
                <label className="access-toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={focusVisible}
                    onChange={() => setFocusVisible(!focusVisible)}
                    aria-label="Toggle focus indicators"
                  />
                  <span className="access-toggle-slider"></span>
                </label>
              </div>
              
              <div className="access-toggle-switch-wrapper">
                <span className="access-toggle-label">
                  <i className="bi bi-mic access-toggle-icon"></i>
                  Voice Navigation
                </span>
                <label className="access-toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={voiceNavigation}
                    onChange={toggleVoiceNavigation}
                    aria-label="Toggle voice navigation"
                  />
                  <span className="access-toggle-slider"></span>
                </label>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="access-actions">
              <button 
                className="access-action-btn"
                onClick={() => setShowStatement(true)}
                aria-label="View accessibility statement"
              >
                <i className="bi bi-file-text access-action-icon"></i>
                Accessibility Statement
              </button>
              
              <button 
                className="access-reset-btn"
                onClick={handleReset}
                aria-label="Reset accessibility settings"
              >
                Reset All Settings
              </button>
            </div>
          </div>
        )}
        
        {/* Accessibility Statement Panel */}
        {showStatement && (
          <div className="access-statement-panel">
            <div className="access-statement-header">
              <h2 className="access-statement-title">Accessibility Statement</h2>
              <button 
                className="access-statement-close"
                onClick={() => setShowStatement(false)}
                aria-label="Close accessibility statement"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            
            <div className="access-statement-content">
              <div className="access-statement-section">
                <h3 className="access-statement-heading">Our Commitment</h3>
                <p className="access-statement-text">
                  COLEE Software Services is committed to ensuring digital accessibility for people 
                  with disabilities. We are continually improving the user experience for everyone, 
                  and applying the relevant accessibility standards.
                </p>
              </div>
              
              <div className="access-statement-section">
                <h3 className="access-statement-heading">Conformance Status</h3>
                <p className="access-statement-text">
                  The Web Content Accessibility Guidelines (WCAG) defines requirements for designers 
                  and developers to improve accessibility for people with disabilities. It defines 
                  three levels of conformance: Level A, Level AA, and Level AAA.
                </p>
                <p className="access-statement-text">
                  Our website is partially conformant with WCAG 2.1 level AA. Partially conformant 
                  means that some parts of the content do not fully conform to the accessibility standard.
                </p>
              </div>
              
              <div className="access-statement-section">
                <h3 className="access-statement-heading">Accessibility Features</h3>
                <p className="access-statement-text">
                  This website offers various accessibility features:
                </p>
                <ul className="access-statement-list">
                  <li className="access-statement-list-item">Adjustable text size</li>
                  <li className="access-statement-list-item">High contrast mode</li>
                  <li className="access-statement-list-item">Dyslexia-friendly text spacing</li>
                  <li className="access-statement-list-item">Reduced motion option</li>
                  <li className="access-statement-list-item">Enhanced focus indicators</li>
                  <li className="access-statement-list-item">Skip to content link</li>
                  <li className="access-statement-list-item">Voice navigation assistance</li>
                  <li className="access-statement-list-item">Keyboard navigation support</li>
                </ul>
              </div>
              
              <div className="access-statement-section">
                <h3 className="access-statement-heading">Feedback</h3>
                <p className="access-statement-text">
                  We welcome your feedback on the accessibility of this website. Please let us know if you 
                  encounter accessibility barriers:
                </p>
                <p className="access-statement-text">
                  <a href="mailto:accessibility@coleesoftware.com" className="access-statement-link">
                    accessibility@coleesoftware.com
                  </a>
                </p>
                <p className="access-statement-text">
                  We try to respond to feedback within 3 business days.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AccessibilityPanel;
